import slugify from 'slugify';
import { db } from '../firebase';
import { doc, getDoc, setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';

/**
 * Generates a URL-friendly slug from a title
 * @param {string} title - The title to convert to a slug
 * @returns {string} - The generated slug
 */
export const generateSlug = (title) => {
  return slugify(title, {
    lower: true,
    strict: true,
    remove: /[*+~.()'"!:@]/g
  });
};

/**
 * Checks if a slug is unique in the blog_slugs collection
 * @param {string} slug - The slug to check
 * @param {string} excludePostId - Optional post ID to exclude from uniqueness check (for updates)
 * @returns {Promise<boolean>} - True if slug is unique, false otherwise
 */
export const isSlugUnique = async (slug, excludePostId = null) => {
  try {
    const slugDoc = await getDoc(doc(db, 'blog_slugs', slug));
    
    if (!slugDoc.exists()) {
      return true; // Slug doesn't exist, so it's unique
    }
    
    // If excluding a specific post ID (for updates), check if it's the same post
    if (excludePostId && slugDoc.data().postId === excludePostId) {
      return true;
    }
    
    return false; // Slug already exists for a different post
  } catch (error) {
    console.error('Error checking slug uniqueness:', error);
    throw new Error('Failed to check slug uniqueness');
  }
};

/**
 * Generates a unique slug by appending numbers if necessary
 * @param {string} baseTitle - The title to generate a slug from
 * @param {string} excludePostId - Optional post ID to exclude from uniqueness check
 * @returns {Promise<string>} - A unique slug
 */
export const generateUniqueSlug = async (baseTitle, excludePostId = null) => {
  let slug = generateSlug(baseTitle);
  let counter = 1;
  
  while (!(await isSlugUnique(slug, excludePostId))) {
    slug = `${generateSlug(baseTitle)}-${counter}`;
    counter++;
  }
  
  return slug;
};

/**
 * Reserves a slug in the blog_slugs collection
 * @param {string} slug - The slug to reserve
 * @param {string} postId - The post ID that owns this slug
 * @returns {Promise<void>}
 */
export const reserveSlug = async (slug, postId) => {
  try {
    await setDoc(doc(db, 'blog_slugs', slug), {
      postId: postId,
      createdAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error reserving slug:', error);
    throw new Error('Failed to reserve slug');
  }
};

/**
 * Releases a slug from the blog_slugs collection
 * @param {string} slug - The slug to release
 * @returns {Promise<void>}
 */
export const releaseSlug = async (slug) => {
  try {
    await deleteDoc(doc(db, 'blog_slugs', slug));
  } catch (error) {
    console.error('Error releasing slug:', error);
    // Don't throw error for cleanup operations
  }
};

/**
 * Updates a slug reservation (for post updates)
 * @param {string} oldSlug - The old slug to release
 * @param {string} newSlug - The new slug to reserve
 * @param {string} postId - The post ID
 * @returns {Promise<void>}
 */
export const updateSlugReservation = async (oldSlug, newSlug, postId) => {
  try {
    // Only update if slugs are different
    if (oldSlug !== newSlug) {
      await reserveSlug(newSlug, postId);
      if (oldSlug) {
        await releaseSlug(oldSlug);
      }
    }
  } catch (error) {
    console.error('Error updating slug reservation:', error);
    throw new Error('Failed to update slug reservation');
  }
};

/**
 * Validates a slug format
 * @param {string} slug - The slug to validate
 * @returns {Object} - Validation result with isValid and error message
 */
export const validateSlug = (slug) => {
  if (!slug) {
    return { isValid: false, error: 'Slug cannot be empty' };
  }
  
  if (slug.length < 3) {
    return { isValid: false, error: 'Slug must be at least 3 characters long' };
  }
  
  if (slug.length > 100) {
    return { isValid: false, error: 'Slug cannot exceed 100 characters' };
  }
  
  // Check for valid slug format (lowercase, alphanumeric, hyphens only)
  const slugRegex = /^[a-z0-9-]+$/;
  if (!slugRegex.test(slug)) {
    return { 
      isValid: false, 
      error: 'Slug can only contain lowercase letters, numbers, and hyphens' 
    };
  }
  
  // Check for consecutive hyphens
  if (slug.includes('--')) {
    return { isValid: false, error: 'Slug cannot contain consecutive hyphens' };
  }
  
  // Check for starting or ending with hyphen
  if (slug.startsWith('-') || slug.endsWith('-')) {
    return { isValid: false, error: 'Slug cannot start or end with a hyphen' };
  }
  
  return { isValid: true };
};
